import type { DashboardDefinition } from '@clockwise/semantic-core';

/**
 * Who a share names. Mirrors `SHARE_AUDIENCES` on the server.
 *
 * `role` is a SYSTEM role (what gates permissions); `service_role` is the role
 * somebody plays on customer work. The first keeps its bare name because it is
 * what every stored share already says.
 */
export const SHARE_AUDIENCES = ['user', 'role', 'service_role', 'everyone'] as const;
export type ShareAudience = (typeof SHARE_AUDIENCES)[number];

export interface DashboardShare {
  id: string;
  dashboardId: string;
  audience: ShareAudience;
  /**
   * A user id for `user`, a system-role id for `role`, a service-role id for
   * `service_role`, `null` for everyone.
   */
  subjectId: string | null;
  /**
   * What the subject is called — a person's display name, or a role's. `null`
   * for `everyone` and for a person or role that is gone; absent from an API
   * older than this client, so a reader must cope without it.
   */
  subjectName?: string | null;
  createdAt: string;
  /** Who added it — the owner, or somebody resharing. */
  createdBy: string | null;
  /** That person's display name, resolved by the server. Absent from an older API. */
  createdByName?: string | null;
  /**
   * Whether the people this share names may share it onward. Set by the owner
   * only. Absent from an API older than this client — read as false.
   */
  canShare?: boolean;
}

export interface Dashboard {
  id: string;
  tenantId: string;
  ownerUserId: string;
  name: string;
  description: string | null;
  /**
   * The dashboard itself — tiles, default period, filter bar. Opening it means
   * running each tile afresh under the viewer's own scope; nothing here is a
   * cached result, which is what makes sharing safe.
   */
  definition: DashboardDefinition;
  createdAt: string;
  updatedAt: string;
  /**
   * True when the CALLER owns it. The field to gate Save, Share, Transfer and
   * Delete on — the server refuses all four for a recipient anyway, so this
   * exists to keep a doomed button off the screen rather than to be the check.
   */
  owned: boolean;
  /**
   * True when the caller may add people to it: the owner, or a recipient whose
   * share is marked `canShare` — in both cases only with `dashboard.share` on their
   * role. Gate Share on this, not on `owned`. Absent from an older API.
   */
  canShare?: boolean;
  /**
   * Who it is shared with — populated for the owner and for a caller who can
   * share. Any other recipient gets `[]`.
   */
  shares: DashboardShare[];
  /** The owner's display name. Absent from an API older than this client. */
  ownerName?: string | null;
}

/** Which slice of the list: yours, other people's, or both. */
export type DashboardListScope = 'mine' | 'shared' | 'all';

export interface ListDashboardsQuery {
  q?: string;
  scope?: DashboardListScope;
  sortField?: 'name' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** Longest dashboard name that will be stored. Mirrors the server's validator. */
export const MAX_DASHBOARD_NAME = 120;
/** Longest dashboard description that will be stored. Mirrors the server's validator. */
export const MAX_DASHBOARD_DESCRIPTION = 500;

export interface CreateDashboardInput {
  name: string;
  description?: string | null;
  definition: DashboardDefinition;
}

export type PatchDashboardInput = Partial<CreateDashboardInput>;

/**
 * "Save as custom report" on a report built on the dashboard.
 *
 * `move` points the tile at the new report; `copy` leaves the tile as it is.
 */
export interface PromoteTileInput {
  name: string;
  mode: 'move' | 'copy';
}

export interface ShareEntryInput {
  audience: ShareAudience;
  subjectId?: string | null;
}

/**
 * An entry to ADD. `canShare` is honoured for the owner only; the server
 * stores what anyone else adds as view-only.
 */
export interface AddShareInput extends ShareEntryInput {
  canShare?: boolean;
}
